import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Column,
  DataTable,
  Filter,
  FilterDefinition,
  QueryParams,
} from '../../components/DataTable';
import { StoreDateTime } from '../../components/StoreDateTime';
import { AuthContext } from '../../context/AuthContext';
import { useStoreTimeZones } from '../../hooks/useStoreTimeZones';
import { ApiService } from '../../services/api';
import {
  CandidatesSearchParams,
  EmbeddingCandidate,
  EmbeddingCandidateResponse,
} from '../../types';
import { ImageZoomModal } from './components/ImageZoomModal';

const TABLE_CONFIG = {
  PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100] as number[],
} as const;

interface EmbeddingsQueryParams {
  chainId: number | null;
  storeId: number | null;
  storeName: string | null;
  imageId: number | null;
  upc: string | null;
}

interface ImageCellProps {
  src: string;
  alt: string;
  onImageClick: (src: string) => void;
}

const ImageCell: React.FC<ImageCellProps> = ({ src, alt, onImageClick }) => (
  <div className="flex justify-center">
    <img
      src={src}
      alt={alt}
      className="h-16 w-16 object-contain cursor-pointer hover:scale-125 transition-transform border border-gray-200 rounded"
      onClick={(e) => {
        e.stopPropagation();
        onImageClick(src);
      }}
    />
  </div>
);

export const CandidatesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const { getTimeZone } = useStoreTimeZones();

  // Parse query parameters into structured data
  const queryParams = useMemo<EmbeddingsQueryParams>(() => {
    const chainIdParam = searchParams.get('chain_id');
    const storeIdParam = searchParams.get('store_id');
    const imageIdParam = searchParams.get('image_id');

    return {
      chainId: chainIdParam ? parseInt(chainIdParam, 10) : null,
      storeId: storeIdParam ? parseInt(storeIdParam, 10) : null,
      storeName: searchParams.get('store_name'),
      imageId: imageIdParam ? parseInt(imageIdParam, 10) : null,
      upc: searchParams.get('upc'),
    };
  }, [searchParams]);

  // Convert query params to DataTable filters
  const dataTableFilters = useMemo<Filter[]>(() => {
    const filters: Filter[] = [];
    if (queryParams.chainId !== null) {
      filters.push({
        field: 'chainId',
        value: queryParams.chainId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.storeId !== null) {
      filters.push({
        field: 'storeId',
        value: queryParams.storeId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.storeName) {
      filters.push({
        field: 'storeName',
        value: queryParams.storeName,
        operator: 'contains',
      });
    }
    if (queryParams.imageId !== null) {
      filters.push({
        field: 'imageId',
        value: queryParams.imageId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.upc) {
      filters.push({
        field: 'normalizedBarcode',
        value: queryParams.upc,
        operator: 'contains',
      });
    }

    return filters;
  }, [queryParams]);

  // Update URL when filters change
  const handleFiltersChange = useCallback(
    (filters: Filter[]) => {
      const newParams = new URLSearchParams(searchParams);

      // Clear filter-related params
      newParams.delete('chain_id');
      newParams.delete('store_id');
      newParams.delete('store_name');
      newParams.delete('image_id');
      newParams.delete('upc');

      // Add new filter params
      filters.forEach((filter) => {
        if (filter.field === 'chainId' && filter.value) {
          newParams.set('chain_id', filter.value);
        } else if (filter.field === 'storeId' && filter.value) {
          newParams.set('store_id', filter.value);
        } else if (filter.field === 'storeName' && filter.value) {
          newParams.set('store_name', filter.value);
        } else if (filter.field === 'imageId' && filter.value) {
          newParams.set('image_id', filter.value);
        } else if (filter.field === 'normalizedBarcode' && filter.value) {
          newParams.set('upc', filter.value);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Define async data fetcher for DataTable
  const fetchCandidates = useCallback(
    async (params: QueryParams) => {
      const { chainId, upc } = queryParams;

      if (!token || !chainId || !upc) {
        return { data: [], total: 0 };
      }

      try {
        // Build filters from DataTable
        const storeIdFilter = params.filters?.find((f) => f.field === 'storeId');
        const storeNameFilter = params.filters?.find((f) => f.field === 'storeName');
        const imageIdFilter = params.filters?.find((f) => f.field === 'imageId');

        const apiParams: CandidatesSearchParams = {
          chainId: chainId,
          upc,
          ...(storeIdFilter?.value && {
            storeId: parseInt(storeIdFilter.value),
          }),
          ...(storeNameFilter?.value && {
            storeName: storeNameFilter.value,
          }),
          ...(imageIdFilter?.value && {
            imageId: parseInt(imageIdFilter.value),
          }),
          page: params.page + 1, // API uses 1-based pagination
          limit: params.pageSize,
        };

        const data = await ApiService.searchCandidates(token, apiParams);

        const candidates: EmbeddingCandidate[] = data.map((candidate) => ({
          ...candidate,
          imageUri: `${candidate.imageUri}?box=${candidate.bboxCoords.join(',')},5`,
          cropImageUri: `${candidate.imageUri}?crop=${candidate.bboxCoords.join(',')}`,
          pogImageUri: `${candidate.pogImageUri}?box=${candidate.pogBoxCoords.join(',')},5`,
          pogCropImageUri: `${candidate.pogImageUri}?crop=${candidate.pogBoxCoords.join(',')}`,
        }));

        return { data: candidates, total: candidates.length }; // Note: API might need to return total count separately
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch embeddings');
        return { data: [], total: 0 };
      }
    },
    [token, queryParams]
  );

  const columns: Column<EmbeddingCandidate>[] = [
    {
      field: 'legibleName',
      title: 'Store',
      sortable: true,
      width: '200px',
    },
    {
      field: 'cameraId',
      title: 'Camera ID',
      sortable: true,
      width: '120px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'tagName',
      title: 'Tag',
      sortable: true,
      width: '120px',
      render: (value: string) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'shelf',
      title: 'Shelf',
      sortable: true,
      width: '80px',
      render: (value: number) => <span className="text-center">{value}</span>,
    },
    {
      field: 'column',
      title: 'Column',
      sortable: true,
      width: '80px',
      render: (value: number) => <span className="text-center">{value}</span>,
    },
    {
      field: 'ilSectionId',
      title: 'Section ID',
      sortable: true,
      width: '120px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'imageId',
      title: 'Image ID',
      sortable: true,
      width: '130px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'imageResolution',
      title: 'Resolution',
      sortable: true,
      width: '100px',
      render: (value: string) => <span className="text-xs font-mono uppercase">{value}</span>,
    },
    {
      field: 'imageCreatedAt',
      title: 'Created at',
      sortable: true,
      width: '220px',
      render: (value: string, row: EmbeddingCandidate) => (
        <StoreDateTime value={value} storeTimeZone={getTimeZone(row.storeId)} className="text-xs" />
      ),
    },
    {
      field: 'pogImageId',
      title: 'POG image ID',
      sortable: true,
      width: '130px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'imageUri',
      title: 'Full Shelf Image',
      width: '100px',
      render: (value: string) => (
        <ImageCell src={value} alt="Full Shelf" onImageClick={setZoomedImage} />
      ),
    },
    {
      field: 'cropImageUri',
      title: 'Shelf Crop',
      width: '100px',
      render: (value: string) => (
        <ImageCell src={value} alt="Shelf Crop" onImageClick={setZoomedImage} />
      ),
    },
    {
      field: 'pogImageUri',
      title: 'Full POG Image',
      width: '100px',
      render: (value: string) => (
        <ImageCell src={value} alt="Full POG" onImageClick={setZoomedImage} />
      ),
    },
    {
      field: 'pogCropImageUri',
      title: 'POG Crop',
      width: '100px',
      render: (value: string) => (
        <ImageCell src={value} alt="POG Crop" onImageClick={setZoomedImage} />
      ),
    },
  ];

  // Define custom filters based on searchEmbeddings API parameters
  const filterDefinitions: FilterDefinition[] = [
    {
      field: 'chainId',
      title: 'Chain ID',
      type: 'number',
      placeholder: 'Enter chain ID (required)',
    },
    {
      field: 'storeId',
      title: 'Store ID',
      type: 'number',
      placeholder: 'Enter store ID',
    },
    {
      field: 'storeName',
      title: 'Store Name',
      type: 'text',
      placeholder: 'Search by store name...',
    },
    {
      field: 'imageId',
      title: 'Image ID',
      type: 'number',
      placeholder: 'Enter image ID',
    },
    {
      field: 'normalizedBarcode',
      title: 'UPC',
      type: 'text',
      placeholder: 'Search by UPC...',
    },
  ];

  // Build comprehensive title with all applied filters
  const dataTableTitle = useMemo(() => {
    const parts: string[] = ['Embeddings Candidates Data'];
    const filters: string[] = [];

    if (queryParams.chainId !== null) {
      filters.push(`Chain: ${queryParams.chainId}`);
    }
    if (queryParams.storeId !== null) {
      filters.push(`Store: ${queryParams.storeId}`);
    }
    if (queryParams.storeName) {
      filters.push(`Store Name: ${queryParams.storeName}`);
    }
    if (queryParams.imageId !== null) {
      filters.push(`Image: ${queryParams.imageId}`);
    }
    if (queryParams.upc) {
      filters.push(`UPC: ${queryParams.upc}`);
    }

    if (filters.length > 0) {
      parts.push(`(${filters.join(', ')})`);
    }

    return parts.join(' ');
  }, [queryParams]);

  return (
    <div className="h-screen bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1 min-h-0">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Error: {error}
          </div>
        )}

        {!queryParams.chainId && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            Please provide chain_id parameter in the URL to fetch embeddings.
          </div>
        )}

        <div className="flex-1 min-h-0">
          <DataTable<EmbeddingCandidate>
            title={dataTableTitle}
            data={fetchCandidates}
            columns={columns}
            pageSize={TABLE_CONFIG.PAGE_SIZE}
            pageSizeOptions={TABLE_CONFIG.PAGE_SIZE_OPTIONS}
            searchable={false}
            filterable={true}
            filterDefinitions={filterDefinitions}
            initialFilters={dataTableFilters}
            onFiltersChange={handleFiltersChange}
            tableKey="candidates-table"
            infinitePagination={true}
            onRowClick={(row: EmbeddingCandidateResponse, rowIndex) => {
              const params = new URLSearchParams();

              if (queryParams.upc) {
                params.set('upc', queryParams.upc);
              }

              params.set('page', rowIndex.toString());
              params.set('box', row.bboxCoords.join(','));

              navigate(`/embeddings/candidates/${row.imageId}?${params.toString()}`);
            }}
          />
        </div>

        <ImageZoomModal
          isOpen={!!zoomedImage}
          imageSrc={zoomedImage || ''}
          onClose={() => setZoomedImage(null)}
        />
      </div>
    </div>
  );
};

export default CandidatesPage;
